import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Switch,
  Radio,
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Divider,
  Input,
  message,
  Upload,
  Tabs,
  Alert,
} from 'antd';
import {
  FileTextOutlined,
  SettingOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SaveOutlined,
  PictureOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { PDFSettingsApiService, PDFSettings, CompanyInfo, PDFLayoutOptions } from '../services/pdfSettingsApi';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface PDFTemplateSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

interface LogoUploadState {
  file: File | null;
  preview: string | null;
  uploading: boolean;
}

const PDFTemplateSettingsModal: React.FC<PDFTemplateSettingsModalProps> = ({
  visible,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewLayout, setPreviewLayout] = useState<'professional' | 'compact'>('professional');
  const [logoState, setLogoState] = useState<LogoUploadState>({
    file: null,
    preview: null,
    uploading: false,
  });
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    name: 'CNF Auto LLC',
    phone: '(555) 123-4567',
    email: 'info@cnfautosuite.com',
  });
  const [hasCustomSettings, setHasCustomSettings] = useState(false);

  useEffect(() => {
    if (visible) {
      // Delay to ensure modal and form are fully rendered before setting values
      setTimeout(() => {
        loadCurrentSettings();
      }, 200);
    }
  }, [visible]);

  const loadCurrentSettings = async () => {
    try {
      const settings = await PDFSettingsApiService.getDefaultSettings();
      const companyInfo = PDFSettingsApiService.mapToCompanyInfo(settings);
      const hasCustom = await PDFSettingsApiService.hasCustomSettings();

      setCompanyInfo(companyInfo);
      setPreviewLayout(settings.layout);
      setHasCustomSettings(hasCustom);

      // Set logo preview if exists
      if (companyInfo.logo) {
        setLogoState({
          file: null,
          preview: companyInfo.logo,
          uploading: false,
        });
      } else {
        setLogoState({
          file: null,
          preview: null,
          uploading: false,
        });
      }

      // Prepare form values
      const formValues = {
        // Layout options
        layout: settings.layout,
        includeCompanyLogo: settings.include_company_logo,
        showInternalNotes: settings.show_internal_notes,
        showPartDetails: settings.show_part_details,
        showDiscountBreakdown: settings.show_discount_breakdown,
        highlightDiscounts: settings.highlight_discounts,
        showOriginalPrices: settings.show_original_prices,
        colorScheme: settings.color_scheme,
        fontFamily: settings.font_family,
        fontSize: settings.font_size,
        spacing: settings.spacing,
        borderStyle: settings.border_style,
        showPageNumbers: settings.show_page_numbers,
        // Company information
        companyName: companyInfo.name,
        companyAddress: companyInfo.address,
        companyCity: companyInfo.city,
        companyState: companyInfo.state,
        companyZip: companyInfo.zip,
        companyPhone: companyInfo.phone,
        companyEmail: companyInfo.email,
        companyWebsite: companyInfo.website,
      };

      console.log('📥 Loaded from backend - Color:', settings.color_scheme, 'Logo:', settings.include_company_logo);

      // Set form values directly - DO NOT call resetFields() as it resets to initial/default values
      form.setFieldsValue(formValues);
      console.log('✅ Form values set. Checking form state...');

      // Verify form has the correct values
      setTimeout(() => {
        const currentValues = form.getFieldsValue(true);
        console.log('🔍 Form state after setting - FULL VALUES:');
        console.log('   - Color:', currentValues.colorScheme);
        console.log('   - Logo:', currentValues.includeCompanyLogo);
        console.log('   - Show Internal Notes:', currentValues.showInternalNotes);
        console.log('   - Show Part Details:', currentValues.showPartDetails);
        console.log('   - Show Discount Breakdown:', currentValues.showDiscountBreakdown);
        console.log('   - Highlight Discounts:', currentValues.highlightDiscounts);
        console.log('   - Show Original Prices:', currentValues.showOriginalPrices);
        console.log('   - Show Page Numbers:', currentValues.showPageNumbers);
        console.log('   - Layout:', currentValues.layout);
        console.log('   - Font Family:', currentValues.fontFamily);
        console.log('🔍 ALL form values:', currentValues);
      }, 100);
    } catch (error) {
      console.error('Failed to load settings:', error);
      message.error('Failed to load current settings');
    }
  };

  const handleLogoUpload = (file: File) => {
    setLogoState({ ...logoState, uploading: true });

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;

      if (!PDFSettingsApiService.validateLogo(result)) {
        message.error('Invalid logo format. Please upload a valid image file.');
        setLogoState({ file: null, preview: null, uploading: false });
        return;
      }

      setLogoState({
        file,
        preview: result,
        uploading: false,
      });

      // Update company info with logo
      setCompanyInfo(prev => ({
        ...prev,
        logo: result,
      }));
    };
    reader.readAsDataURL(file);
    return false; // Prevent upload
  };

  const handleRemoveLogo = () => {
    setLogoState({
      file: null,
      preview: null,
      uploading: false,
    });
    setCompanyInfo(prev => ({
      ...prev,
      logo: undefined,
    }));
  };

  const handleSaveSettings = async (values: any) => {
    try {
      setLoading(true);

      console.log('💾 SAVE INITIATED');
      console.log('📋 Raw form values received:', values);
      console.log('🔍 Specific values - Color:', values.colorScheme, 'Logo:', values.includeCompanyLogo, 'Layout:', values.layout);

      // Prepare settings for API
      const settingsToSave = {
        // Layout options
        layout: values.layout,
        include_company_logo: values.includeCompanyLogo,
        show_internal_notes: values.showInternalNotes,
        show_part_details: values.showPartDetails,
        show_discount_breakdown: values.showDiscountBreakdown,
        highlight_discounts: values.highlightDiscounts,
        show_original_prices: values.showOriginalPrices,
        color_scheme: values.colorScheme,
        font_family: values.fontFamily,
        font_size: values.fontSize,
        spacing: values.spacing,
        border_style: values.borderStyle,
        show_page_numbers: values.showPageNumbers,
        // Company information
        company_name: values.companyName,
        company_address: values.companyAddress,
        company_city: values.companyCity,
        company_state: values.companyState,
        company_zip: values.companyZip,
        company_phone: values.companyPhone,
        company_email: values.companyEmail,
        company_website: values.companyWebsite,
        company_logo: companyInfo.logo, // Keep the logo from state since it's handled separately
      };

      console.log('📦 Full settings object being sent to API:', JSON.stringify(settingsToSave, null, 2));

      // Save settings via API
      console.log('🚀 About to call PDFSettingsApiService.updateDefaultSettings...');
      const savedSettings = await PDFSettingsApiService.updateDefaultSettings(settingsToSave);
      console.log('✅ API call completed. Backend returned:', savedSettings);

      console.log('✅ Settings saved successfully');

      setHasCustomSettings(true);
      message.success('PDF template settings saved successfully!');

      // Close modal after save to ensure clean state on next open
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('❌ ERROR saving settings:', error);
      console.error('❌ Error details:', JSON.stringify(error, null, 2));
      if (error instanceof Error) {
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefaults = () => {
    Modal.confirm({
      title: 'Reset to Default Settings',
      content: 'Are you sure you want to reset all PDF template settings to default values? This action cannot be undone.',
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await PDFSettingsApiService.resetToDefault();
          await loadCurrentSettings();
          message.success('Settings reset to defaults successfully!');
        } catch (error) {
          console.error('Error resetting settings:', error);
          message.error('Failed to reset settings');
        }
      },
    });
  };

  const handleExportSettings = async () => {
    try {
      const settings = await PDFSettingsApiService.getDefaultSettings();
      const settingsJson = JSON.stringify(settings, null, 2);
      const blob = new Blob([settingsJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cnf-pdf-template-settings.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('Settings exported successfully!');
    } catch (error) {
      console.error('Error exporting settings:', error);
      message.error('Failed to export settings');
    }
  };

  const handleImportSettings = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const jsonString = e.target?.result as string;
        const importedSettings = JSON.parse(jsonString);

        // Validate the imported settings structure
        if (!importedSettings || typeof importedSettings !== 'object') {
          throw new Error('Invalid settings format');
        }

        await PDFSettingsApiService.updateDefaultSettings(importedSettings);
        await loadCurrentSettings();
        message.success('Settings imported successfully!');
      } catch (error) {
        console.error('Error importing settings:', error);
        message.error('Failed to import settings. Please check the file format.');
      }
    };
    reader.readAsText(file);
    return false; // Prevent upload
  };

  const layoutPreviewData = {
    professional: {
      title: 'Professional Layout',
      description: 'Full-featured layout with detailed company information, customer details, and comprehensive service breakdown.',
      features: [
        'Company header with logo',
        'Detailed customer & vehicle info',
        'Complete service breakdown',
        'Parts details (expandable)',
        'Terms & conditions',
        'Professional footer',
      ],
    },
    compact: {
      title: 'Compact Layout',
      description: 'Streamlined layout optimized for quick printing and minimal paper usage.',
      features: [
        'Condensed header',
        'Summary customer info',
        'Simple service list',
        'Essential totals only',
        'Minimal spacing',
        'One-page friendly',
      ],
    },
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          PDF Template Settings
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      centered
      style={{ top: 20 }}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Default PDF Template Settings"
          description="Configure your default PDF template settings. These will be used as defaults for all new estimates, but you can still customize individual PDFs when generating them."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {hasCustomSettings && (
          <Alert
            message="Custom Settings Active"
            description="You have custom PDF template settings configured. You can reset to defaults or export your settings for backup."
            type="success"
            showIcon
          />
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveSettings}
      >
        <Tabs defaultActiveKey="layout">
          <TabPane tab="Layout & Styling" key="layout">
            <Row gutter={[24, 0]}>
              {/* Layout Selection */}
              <Col span={12}>
                <Form.Item
                  label="PDF Layout"
                  name="layout"
                  tooltip="Choose between professional or compact layout"
                >
                  <Radio.Group
                    onChange={(e) => setPreviewLayout(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="professional">Professional</Radio.Button>
                    <Radio.Button value="compact">Compact</Radio.Button>
                  </Radio.Group>
                </Form.Item>

                <Card
                  size="small"
                  title={layoutPreviewData[previewLayout].title}
                  style={{ marginBottom: 16 }}
                >
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {layoutPreviewData[previewLayout].description}
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    {layoutPreviewData[previewLayout].features.map((feature, index) => (
                      <div key={index} style={{ fontSize: '11px', color: '#666' }}>
                        • {feature}
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Color Scheme"
                  name="colorScheme"
                  tooltip="Choose the primary color for headers and accents"
                >
                  <Select>
                    <Select.Option value="blue">🔵 Blue Theme</Select.Option>
                    <Select.Option value="green">🟢 Green Theme</Select.Option>
                    <Select.Option value="gray">⚫ Gray Theme</Select.Option>
                    <Select.Option value="red">🔴 Red Theme</Select.Option>
                    <Select.Option value="purple">🟣 Purple Theme</Select.Option>
                    <Select.Option value="orange">🟠 Orange Theme</Select.Option>
                  </Select>
                </Form.Item>

                <Row gutter={[12, 12]}>
                  <Col span={24}>
                    <Form.Item
                      name="includeCompanyLogo"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Include company logo</Text>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="showPartDetails"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Show detailed parts breakdown</Text>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="showInternalNotes"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Include internal notes</Text>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="showPageNumbers"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Show page numbers</Text>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Discount Display Options */}
                <Divider style={{ margin: '16px 0' }} />
                <Title level={5} style={{ marginBottom: '12px' }}>
                  Discount Display Options
                </Title>
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <Form.Item
                      name="showDiscountBreakdown"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Show detailed discount breakdown</Text>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="highlightDiscounts"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Highlight discounted items</Text>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="showOriginalPrices"
                      valuePropName="checked"
                      style={{ marginBottom: 8 }}
                    >
                      <Switch size="small" />
                      <Text style={{ marginLeft: 8, fontSize: '12px' }}>Show original prices with strikethrough</Text>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {/* Typography & Styling Options */}
              <Col span={24}>
                <Divider />
                <Title level={4}>Typography & Styling</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Customize fonts, sizes, and spacing for your PDF
                </Text>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Font Family"
                  name="fontFamily"
                  tooltip="Choose the font style for your PDF"
                >
                  <Select size="small">
                    <Select.Option value="helvetica">Helvetica (Modern)</Select.Option>
                    <Select.Option value="times">Times (Traditional)</Select.Option>
                    <Select.Option value="courier">Courier (Monospace)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Font Size"
                  name="fontSize"
                  tooltip="Adjust the overall size of text in the PDF"
                >
                  <Select size="small">
                    <Select.Option value="small">Small</Select.Option>
                    <Select.Option value="medium">Medium</Select.Option>
                    <Select.Option value="large">Large</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Spacing"
                  name="spacing"
                  tooltip="Control line spacing and section gaps"
                >
                  <Select size="small">
                    <Select.Option value="tight">Tight</Select.Option>
                    <Select.Option value="normal">Normal</Select.Option>
                    <Select.Option value="loose">Loose</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Border Style"
                  name="borderStyle"
                  tooltip="Choose the border style for tables and sections"
                >
                  <Select size="small">
                    <Select.Option value="none">No Borders</Select.Option>
                    <Select.Option value="simple">Simple Lines</Select.Option>
                    <Select.Option value="rounded">Rounded Corners</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

        <TabPane tab="Company Information" key="company">
          <Row gutter={[24, 0]}>
            <Col span={12}>
              <Form.Item
                label="Company Name"
                name="companyName"
                rules={[{ required: true, message: 'Company name is required' }]}
              >
                <Input placeholder="CNF Auto LLC" size="small" />
              </Form.Item>

              <Form.Item
                label="Address"
                name="companyAddress"
              >
                <Input placeholder="123 Main Street" size="small" />
              </Form.Item>

              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item
                    label="City"
                    name="companyCity"
                  >
                    <Input placeholder="City" size="small" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="State"
                    name="companyState"
                  >
                    <Input placeholder="State" size="small" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="ZIP"
                    name="companyZip"
                  >
                    <Input placeholder="12345" size="small" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Phone Number"
                name="companyPhone"
                rules={[{ required: true, message: 'Phone number is required' }]}
              >
                <Input placeholder="(555) 123-4567" size="small" />
              </Form.Item>

              <Form.Item
                label="Email Address"
                name="companyEmail"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="info@cnfautosuite.com" size="small" />
              </Form.Item>

              <Form.Item
                label="Website (optional)"
                name="companyWebsite"
              >
                <Input placeholder="www.cnfautosuite.com" size="small" />
              </Form.Item>

              {/* Logo Upload Section */}
              <div style={{ marginTop: 8 }}>
                <Text style={{ fontSize: '12px', marginBottom: 4, display: 'block' }}>Company Logo</Text>
                {logoState.preview ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img
                      src={logoState.preview}
                      alt="Logo preview"
                      style={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid #d9d9d9', borderRadius: 4 }}
                    />
                    <Button size="small" onClick={handleRemoveLogo} icon={<DeleteOutlined />}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleLogoUpload(file);
                      };
                      input.click();
                    }}
                    loading={logoState.uploading}
                  >
                    Upload Logo
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Import/Export" key="importexport">
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card title="Backup & Restore Settings" size="small">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div style={{ textAlign: 'center' }}>
                      <DownloadOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                      <div>
                        <Text strong>Export Settings</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Download your current PDF template settings as a backup file
                        </Text>
                      </div>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleExportSettings}
                        style={{ marginTop: 12 }}
                      >
                        Export Settings
                      </Button>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ textAlign: 'center' }}>
                      <UploadOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
                      <div>
                        <Text strong>Import Settings</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Restore PDF template settings from a backup file
                        </Text>
                      </div>
                      <Upload
                        accept=".json"
                        beforeUpload={handleImportSettings}
                        showUploadList={false}
                        style={{ marginTop: 12 }}
                      >
                        <Button icon={<UploadOutlined />}>
                          Import Settings
                        </Button>
                      </Upload>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Reset Settings" size="small">
                <div style={{ textAlign: 'center' }}>
                  <ReloadOutlined style={{ fontSize: 32, color: '#fa8c16', marginBottom: 8 }} />
                  <div>
                    <Text strong>Reset to Defaults</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Reset all PDF template settings to their original default values
                    </Text>
                  </div>
                  <Button
                    danger
                    icon={<ReloadOutlined />}
                    onClick={handleResetToDefaults}
                    style={{ marginTop: 12 }}
                  >
                    Reset to Defaults
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
      </Form>

      {/* Actions */}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            icon={<SaveOutlined />}
            onClick={() => {
              console.log('Save button clicked');
              console.log('Current form values:', form.getFieldsValue(true));
              form.validateFields()
                .then(() => {
                  // Get ALL form values (including untouched fields) after validation passes
                  const allValues = form.getFieldsValue(true);
                  console.log('Form validation passed. All values:', allValues);
                  console.log('Calling handleSaveSettings directly');
                  handleSaveSettings(allValues);
                })
                .catch(errorInfo => {
                  console.log('Form validation failed:', errorInfo);
                  message.error('Please fill in all required fields in the Company Information tab.');
                });
            }}
          >
            Save Settings
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default PDFTemplateSettingsModal;